import type { AdvanceDirectiveModel } from './types';
import { deleteAdvanceDirectiveOptions, getAdvanceDirectivesOptions, type DeleteAdvanceDirectiveCommand } from '@generated';
import { useMutation } from '~/util/http/useMutation';
import { AdvanceDirectiveFormModal } from './forms/AdvanceDirectiveFormModal';
import { DataTable } from '~/shared/data-tables/DataTable';
import type { DataTableColumnProps } from '~/shared/data-tables/DataTableColumn';
import { TableActions } from '~/shared/data-tables/TableActions';

function RenderActionsCell(props: AdvanceDirectiveModel) {
	const deleteDirective = useMutation<void, DeleteAdvanceDirectiveCommand>({
		...deleteAdvanceDirectiveOptions,
		invalidateQueryKeys: [getAdvanceDirectivesOptions(props.stayId).queryKey]
	});
	const onRemove = () => {
		if (confirm('Are you sure you want to remove this directive?')) {
			deleteDirective.mutate({ stayId: props.stayId, advanceDirectiveId: props.id });
		}
	};
	return (
		<TableActions>
			<TableActions.ViewButton
				renderModal={closeModal => <AdvanceDirectiveFormModal stayId={props.stayId} existingDirective={props} closeModal={closeModal} />}
			/>
			<TableActions.DeleteButton onClick={onRemove} isPending={deleteDirective.isPending} />
		</TableActions>
	);
}

const columns: DataTableColumnProps<AdvanceDirectiveModel>[] = [
	{
		header: 'Directive Type',
		key: 'advanceDirectiveTypeLabel'
	},
	{
		header: 'Attached Contact',
		key: 'attachedContactId',
		renderCell: d => d.attachedContactName ?? '-'
	},
	{
		header: 'Attached Document',
		key: 'attachedDocumentId',
		renderCell: d =>
			d.attachedDocumentId && d.attachedDocument
				? d.attachedDocument.originalDocumentName
				: d.awaitingDocumentation
				? 'Awaiting Documentation'
				: ''
	},
	{
		key: 'actions',
		header: 'Actions',
		width: 'w-24',
		renderCell: d => <RenderActionsCell {...d} />
	}
];

export function AdvanceDirectivesTable({ rows }: { rows: AdvanceDirectiveModel[] }) {
	return (
		<DataTable
			columns={columns}
			rows={rows}
			viewMore={{
				text: 'View Additional Directives',
				threshold: 3
			}}
			getRowNotes={x => x.notes ?? null}
		/>
	);
}
